import { Fragment, useState, type ReactNode } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Loader2,
  Package,
  Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useCreateKitProcedure,
  useKitForms,
  useKitInventory,
  useKitTemplates,
} from '@/hooks/usePickLists'
import { PickListActionsMenu } from '@/components/pick-lists/PickListActionsMenu'
import { formatSheetDate, lotExpiryStatus } from '@/lib/pickList'
import type { KitRow } from '@/types/pickList'
import { procedureRunPath } from '@/lib/procedureRunPath'

type KitsView = 'inventory' | 'forms'

/**
 * Inventory → Kits, per spec p3.
 *
 *   Kit Forms     — kit builds still to be done; the operator picks one and works it.
 *   Kit Inventory — completed kits, each with its calculated expiry and the
 *                   component lots that went into it.
 *
 * A "kit" is a procedure containing a form whose type is Kit; the form's name is
 * the kit name. Kit-only procedures are hidden from the Procedures page and
 * appear here instead.
 */
export function KitsTab() {
  const [view, setView] = useState<KitsView>('inventory')
  const [createOpen, setCreateOpen] = useState(false)
  const inventory = useKitInventory()
  const forms = useKitForms()

  const active = view === 'inventory' ? inventory : forms
  const rows = active.data ?? []

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center gap-2'>
        <SubTab
          active={view === 'inventory'}
          onClick={() => setView('inventory')}
          icon={<ClipboardList className='h-4 w-4' />}
          label='Kit Inventory'
          count={inventory.data?.length}
        />
        <SubTab
          active={view === 'forms'}
          onClick={() => setView('forms')}
          icon={<Package className='h-4 w-4' />}
          label='Kit Forms'
          count={forms.data?.length}
        />
        {/* Starting a kit belongs wherever kits are — an operator looking at the
            shelf is the one who notices it needs restocking, and sending them
            to Kits → Create kit order to act on that is a detour. Deliberately
            NOT a change to either listing: an order that has just been placed is
            in progress, so it appears under Kit Forms, and Kit Inventory stays
            completed kits only. */}
        <Button size='sm' className='ml-auto' onClick={() => setCreateOpen(true)}>
          <Plus className='mr-2 h-4 w-4' />
          Create kit order
        </Button>
      </div>

      <NewKitRunDialog open={createOpen} onOpenChange={setCreateOpen} />

      <p className='text-xs text-muted-foreground'>
        {view === 'inventory'
          ? 'Completed kits. Expiry is the earliest expiry among the component lots, derived from what was actually used.'
          : 'Kit builds still in progress. Open one to work its forms.'}
      </p>

      {active.isLoading ? (
        <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading kits…
        </div>
      ) : active.error ? (
        <p className='text-sm text-destructive'>
          {active.error instanceof Error ? active.error.message : 'Could not load kits.'}
        </p>
      ) : (
        <KitTable rows={rows} view={view} />
      )}
    </div>
  )
}

/**
 * Start a kit run from a KIT-typed procedure template.
 *
 * The same operation the Kits page offers, reached from the shelf. No patient
 * and no assignee: creating the procedure is not an assignment — to execute the
 * kit you assign it afterwards, on the run itself.
 *
 * Templates that are typed KIT but not shaped like one come back `runnable:
 * false` with a reason, and are listed disabled rather than filtered out.
 * Hiding them is indistinguishable from the template having failed to save.
 */
function NewKitRunDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  // Only fetched once the dialog is opened — the tab itself never needs it.
  const templates = useKitTemplates(open)
  const createKit = useCreateKitProcedure()
  const [selectedId, setSelectedId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const rows = templates.data ?? []
  const chosen = rows.find(t => t.id === selectedId)

  const close = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      setSelectedId('')
      setError(null)
    }
  }

  const submit = async () => {
    if (!chosen?.runnable) return
    setError(null)
    try {
      const build = await createKit.mutateAsync({ procedureTemplateId: chosen.id })
      close(false)
      navigate(`/kits/runs/${build.procedureId}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the kit order.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Create kit order</DialogTitle>
          <DialogDescription>
            Pick a kit template to build. The order opens so you can assign and work it; it appears
            under Kit Forms until it is finished, then here in Kit Inventory.
          </DialogDescription>
        </DialogHeader>

        {templates.isLoading ? (
          <div className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading kit templates…
          </div>
        ) : templates.error ? (
          <p className='py-4 text-sm text-destructive'>
            {templates.error instanceof Error
              ? templates.error.message
              : 'Could not load kit templates.'}
          </p>
        ) : rows.length === 0 ? (
          <p className='py-4 text-sm text-muted-foreground'>
            No kit templates yet. A kit template is a procedure template typed <strong>KIT
            Creation</strong> whose steps are kit forms — create one in the Form Builder, then
            sequence it into a procedure template.
          </p>
        ) : (
          <ul className='max-h-72 space-y-1.5 overflow-y-auto'>
            {rows.map(t => {
              const picked = t.id === selectedId
              return (
                <li key={t.id}>
                  <button
                    type='button'
                    disabled={!t.runnable}
                    onClick={() => setSelectedId(t.id)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2 text-left transition-colors',
                      !t.runnable
                        ? 'cursor-not-allowed border-gray-200 bg-muted/30 opacity-70'
                        : picked
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:bg-muted/40',
                    )}
                  >
                    <div className='flex items-center justify-between gap-2'>
                      <span className='truncate text-sm font-medium'>{t.name}</span>
                      <span className='shrink-0 text-xs tabular-nums text-muted-foreground'>
                        {t.stepCount} step{t.stepCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    {t.formNames.length > 0 && (
                      <p className='mt-0.5 truncate text-xs text-muted-foreground'>
                        {t.formNames.join(' → ')}
                      </p>
                    )}
                    {!t.runnable && (
                      <p className='mt-1 text-xs font-medium text-amber-700'>
                        {t.blockedReason ?? 'This template cannot be run.'}
                      </p>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {error && (
          <div className='flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
            <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
            {error}
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => close(false)} disabled={createKit.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!chosen?.runnable || createKit.isPending}>
            {createKit.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            Create kit order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SubTab({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  count?: number
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
      )}
    >
      {icon}
      {label}
      {count !== undefined && (
        <span className={cn('tabular-nums', active ? 'text-primary-foreground/70' : 'text-gray-400')}>
          {count}
        </span>
      )}
    </button>
  )
}

// Per-user column layout for the kit list. The expand chevron, Kit ID and the
// actions cell are locked. See src/components/table/index.ts for the pattern.
const KITS_TABLE_ID = 'kits'

const KITS_COLUMNS: TableColumnDef[] = [
  { id: 'expand', header: 'Components', locked: true },
  { id: 'kitId', header: 'Kit ID', locked: true, sortable: true },
  { id: 'kitName', header: 'Kit name', sortable: true },
  { id: 'kitExpiry', header: 'Kit expiration', sortable: true },
  { id: 'components', header: 'Components count', sortable: true },
  { id: 'progress', header: 'Progress', sortable: true },
  { id: 'status', header: 'Status', sortable: true },
  { id: 'actions', header: 'Open', locked: true },
]

// Header sorting (z8nrz7e4h6). An expanded component list is rendered with its
// kit row, so it moves with it.
const KITS_SORT: SortAccessors<KitRow> = {
  kitId: r => r.kitSupplyId,
  kitName: r => r.kitName,
  kitExpiry: r => r.kitExpiry,
  components: r => (r.components.length === 0 ? null : r.components.length),
  progress: r => (r.totalForms > 0 ? r.completedForms / r.totalForms : 0),
  status: r => r.completedForms,
}

const KIT_HEAD_CLASSES: Record<string, string | undefined> = {
  expand: 'w-8 px-2 py-2.5',
  actions: 'w-32 px-4 py-2.5',
}

// Kit expiration says how it is derived, so its header is not just its name.
const KIT_HEAD_CONTENT: Record<string, ReactNode> = {
  expand: null,
  kitId: 'Kit ID',
  kitName: 'Kit name',
  kitExpiry: (
    <>
      Kit expiration <span className='normal-case italic'>· calculated</span>
    </>
  ),
  components: 'Components',
  progress: 'Progress',
  status: 'Status',
  actions: null,
}

/** A kit row's identity: its build, else (a run with no live build) its run. */
function kitRowKey(row: KitRow): string {
  return row.buildId ?? `run:${row.procedureId}`
}

function KitTable({ rows, view }: { rows: KitRow[]; view: KitsView }) {
  // Before the empty-state early return below: a hook cannot be called
  // conditionally.
  const cols = useTableColumns(KITS_TABLE_ID, KITS_COLUMNS)
  const sortedRows = useSortedRows(rows, cols.sort, KITS_SORT)
  const navigate = useNavigate()
  // One "which row is open" value, not per-row state: independent state let
  // several menus sit open at once and none closed when another opened.
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null)
  // Same single-value rule for the expanded row, matching the procedures table.
  const [expandedId, setExpandedId] = useState<string | null>(null)

  /**
   * The two sub-tabs ask different questions, so they open different places.
   *
   * Kit Inventory is finished stock: the row opens the kit's own RECORD — what
   * went into it, what built it, who signed. Sending it to the run instead
   * showed a procedure page with steps and an Initiate button, which answers
   * "what do I do next" about work that is already done.
   *
   * Kit Forms is work in progress, so it still opens the run. Not
   * `/procedures/:id`: that route does not exist, and the router's catch-all
   * silently redirects anything unmatched to the dashboard, so a wrong URL
   * here would read as "the row does nothing" rather than 404ing.
   *
   * A completed row with no buildId cannot have a record to show, so it falls
   * back to the run rather than routing to a page that would 404.
   */
  // A kit built inside a Transplant or MES run (a step with Process Output =
  // KIT, z8nrz7f1dz) is worked on that run's own page, not the kit run page.
  const runPath = (row: KitRow) =>
    (row.procedureType && row.procedureType !== 'KitCreation'
      ? procedureRunPath(row.procedureType, row.procedureId, row.patientMrn)
      : null) ?? `/kits/runs/${row.procedureId}`
  const open = (row: KitRow) =>
    navigate(view === 'inventory' && row.buildId ? `/inventory/kits/completed/${row.buildId}` : runPath(row))

  if (rows.length === 0) {
    return (
      <div className='flex flex-col items-center gap-2 rounded-xl border bg-card p-12 text-center'>
        <Package className='h-8 w-8 text-muted-foreground/40' />
        <p className='text-sm font-medium'>
          {view === 'inventory' ? 'No completed kits yet' : 'No kit builds in progress'}
        </p>
        <p className='max-w-md text-xs text-muted-foreground'>
          A kit is a form whose type is <strong>Kit</strong>. Create one in the Form Builder, add
          it to a procedure template, then start that procedure.
        </p>
      </div>
    )
  }

  return (
    <>
    <div className='overflow-x-persistent rounded-xl border bg-card'>
      <table className='w-full min-w-[900px] text-sm'>
        <thead>
          <tr className='border-b bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground'>
            {cols.visible.map(({ def }) => (
              <ResizableTh
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className={KIT_HEAD_CLASSES[def.id] ?? 'px-4 py-2.5 text-left font-semibold'}
                resizable={def.id !== 'expand' && def.id !== 'actions'}
              >
                {KIT_HEAD_CONTENT[def.id]}
              </ResizableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => {
            const pct =
              row.totalForms > 0 ? Math.round((row.completedForms / row.totalForms) * 100) : 0
            const exp = lotExpiryStatus(row.kitExpiry)
            // One row per KIT, and a run can make several (z8nrz7e11d), so the
            // build — not the run — identifies the row.
            const rowKey = kitRowKey(row)
            const expanded = expandedId === rowKey
            return (
              <Fragment key={rowKey}>
              <tr
                role='link'
                tabIndex={0}
                onClick={() => open(row)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    open(row)
                  }
                }}
                className={cn(
                  'cursor-pointer border-b transition-colors last:border-0 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                  i % 2 === 1 && 'bg-muted/10',
                )}
              >
                <ColumnCells
                  columns={cols}
                  cells={{
                    expand: (
                      <td className='px-2 py-3'>
                        {/* Expands the row in place to show what went INTO this kit —
                            spec p3: "we will need to show the list of LOTs and
                            material involved in the KIT". Stops propagation because
                            the row itself is a link to the kit's full record. */}
                        <button
                          type='button'
                          title={expanded ? 'Hide components' : 'Show components'}
                          aria-expanded={expanded}
                          onClick={e => {
                            e.stopPropagation()
                            setExpandedId(expanded ? null : rowKey)
                          }}
                          className='flex h-6 w-6 items-center justify-center rounded-md hover:bg-muted'
                        >
                          {expanded ? (
                            <ChevronDown className='h-4 w-4 text-muted-foreground' />
                          ) : (
                            <ChevronRight className='h-4 w-4 text-muted-foreground' />
                          )}
                        </button>
                      </td>
                    ),
                    kitId: (
                      <td className='px-4 py-3'>
                        {/* Spec p3: "Patient = LOT Number" — a kit is identified by
                            its supply id, not by a person. Until a build exists there
                            is no supply id to show, and saying so beats showing an
                            MRN that means nothing for a kit. */}
                        {row.kitSupplyId ? (
                          <div className='font-mono text-xs font-semibold'>{row.kitSupplyId}</div>
                        ) : (
                          <div className='text-xs italic text-muted-foreground'>not yet built</div>
                        )}
                      </td>
                    ),
                    kitName: <td className='px-4 py-3 font-semibold'>{row.kitName}</td>,
                    kitExpiry: (
                      <td className='px-4 py-3'>
                        {row.kitExpiry ? (
                          <span
                            className={cn(
                              'tabular-nums',
                              exp?.expired && 'font-bold text-red-700',
                              exp?.nearExpiry && 'font-bold text-amber-600',
                            )}
                          >
                            {formatSheetDate(row.kitExpiry)}
                          </span>
                        ) : (
                          <span className='text-xs text-muted-foreground'>
                            — no components recorded
                          </span>
                        )}
                      </td>
                    ),
                    components: (
                      <td className='px-4 py-3 text-xs text-muted-foreground'>
                        {row.components.length === 0
                          ? '—'
                          : `${row.components.length} lot${row.components.length === 1 ? '' : 's'}`}
                      </td>
                    ),
                    progress: (
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-2'>
                          <div className='h-1.5 w-20 overflow-hidden rounded-full bg-slate-200'>
                            <div
                              className='h-full rounded-full bg-emerald-500'
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className='text-xs tabular-nums text-muted-foreground'>
                            {row.completedForms}/{row.totalForms}
                          </span>
                        </div>
                      </td>
                    ),
                    status: (
                      <td className='px-4 py-3'>
                        <span className='inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600'>
                          {row.completedForms}/{row.totalForms} steps done
                        </span>
                      </td>
                    ),
                    actions: (
                      <td className='px-4 py-3'>
                        <div className='flex items-center justify-end gap-1'>
                          {/* Only on Kit Forms. A pick list says "go and fetch
                              these"; for a completed kit the components are already
                              collected and drawn off the shelf, so the sheet would
                              be printing history. */}
                          {view === 'forms' && (
                            <>
                              <PickListActionsMenu
                                scope={{ scope: 'PROCEDURE', procedureId: row.procedureId }}
                                open={openMenuRowId === rowKey}
                                onOpenChange={next =>
                                  setOpenMenuRowId(next ? rowKey : null)
                                }
                              />
                            </>
                          )}
                          <ChevronRight className='h-4 w-4 text-muted-foreground/40' />
                        </div>
                      </td>
                    ),
                  }}
                />
              </tr>

              {expanded && (
                <tr className='border-b bg-muted/20 last:border-0'>
                  <td colSpan={cols.visible.length} className='px-6 py-3'>
                    {row.components.length === 0 ? (
                      <p className='py-1 text-xs text-muted-foreground'>
                        No component lots recorded for this kit yet.
                      </p>
                    ) : (
                      <div>
                        <p className='mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                          Components · {row.components.length} lot
                          {row.components.length === 1 ? '' : 's'}
                        </p>
                        <ul className='divide-y'>
                          {row.components.map((c, ci) => {
                            // The kit expires with its EARLIEST component, so
                            // the one that set it is worth pointing at.
                            const drives =
                              !!row.kitExpiry && !!c.expiryDate && c.expiryDate === row.kitExpiry
                            const cExp = lotExpiryStatus(c.expiryDate)
                            return (
                              <li
                                key={`${c.lotNumber}-${c.materialId}-${ci}`}
                                className='flex items-center gap-3 py-1.5 text-xs'
                              >
                                <span className='w-32 shrink-0 font-mono font-semibold'>
                                  {c.materialCode}
                                </span>
                                <span className='min-w-0 flex-1 truncate'>{c.materialName}</span>
                                <span className='w-36 shrink-0 font-mono text-muted-foreground'>
                                  {c.lotNumber}
                                </span>
                                <span className='w-16 shrink-0 tabular-nums text-muted-foreground'>
                                  {c.qty}
                                </span>
                                <span
                                  className={cn(
                                    'w-40 shrink-0 tabular-nums',
                                    cExp?.expired && 'font-bold text-red-700',
                                    cExp?.nearExpiry && 'font-bold text-amber-600',
                                  )}
                                >
                                  {formatSheetDate(c.expiryDate)}
                                  {drives && (
                                    <span className='ml-1.5 text-[10px] font-semibold uppercase text-muted-foreground'>
                                      · min
                                    </span>
                                  )}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    )}
                  </td>
                </tr>
              )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
    </>
  )
}
